import { RETRY } from './constants';

export class SharePointError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly detail?: string
  ) {
    super(message);
    this.name = 'SharePointError';
  }
}

function isRetryable(error: unknown): boolean {
  if (error instanceof SharePointError) {
    return error.statusCode === 429 || error.statusCode === 503;
  }
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  opts: {
    maxAttempts?: number;
    initialDelayMs?: number;
    backoffFactor?: number;
  } = {}
): Promise<T> {
  const maxAttempts = opts.maxAttempts ?? RETRY.MAX_ATTEMPTS;
  const initialDelay = opts.initialDelayMs ?? RETRY.INITIAL_DELAY_MS;
  const factor = opts.backoffFactor ?? RETRY.BACKOFF_FACTOR;

  let delay = initialDelay;
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (attempt < maxAttempts && isRetryable(err)) {
        await sleep(delay);
        delay *= factor;
      } else {
        break;
      }
    }
  }

  throw lastError;
}

/**
 * Runs tasks with a bounded concurrency limit.
 * Preserves result order; any rejection rejects the whole batch.
 */
export async function pooledAll<T>(
  tasks: Array<() => Promise<T>>,
  concurrency: number
): Promise<T[]> {
  const results: T[] = new Array<T>(tasks.length);
  let index = 0;

  async function worker(): Promise<void> {
    while (index < tasks.length) {
      const current = index++;
      results[current] = await tasks[current]();
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, worker);
  await Promise.all(workers);
  return results;
}
